import { Dispatch, ReactNode, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

import FileCard from '@/design_system/FileCard/FileCard';
import InputFile, { InputFileProps } from '@/design_system/InputFile';
import Message from '@/design_system/Message';
import { PhotoCardGrid } from '@/design_system/PhotoCard';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { CreateMediumBody, Medium } from '@/models/medium';
import useViewPort from '@/utils/useViewport';

import QrCodeDialog, { useCreateQrCodeUrl } from './QrCodeDialog';
import { FileWithError, FileWithMediumId, useFileUploader } from './useFileUploader';

type FileUploadProps = Omit<InputFileProps, 'ariaLabel' | 'onUpload' | 'variant'> & {
  uploadData: CreateMediumBody;
  ariaLabel?: string;
  label?: ReactNode;
  media?: (Medium & { customIcon?: ReactNode })[];
  maxNumberOfMedia?: number;
  onChange?: Dispatch<SetStateAction<Medium[]>>;
  onUpload?: (medium: Medium) => void;
  onDelete?: (mediumId: string) => void;
  deleteWithApi?: boolean;
  disabled?: boolean;
  showDisabledInput?: boolean;
  theme?: 'default' | 'brand';
  variant?: 'default' | 'grid';
  labelFontWeight?: 'medium' | 'regular';
  onMount?: (ref: RefObject<HTMLDivElement | null>) => void;
  error?: string;
  style?: React.CSSProperties;
  secondaryActionIconOnly?: boolean;
  withQrCode?: boolean;
};

const FileUpload = ({
  ariaLabel,
  type,
  uploadData,
  label,
  prompt,
  icon,
  media = [],
  maxNumberOfMedia,
  onChange,
  onUpload,
  onDelete,
  deleteWithApi = false,
  disabled,
  showDisabledInput,
  theme = 'default',
  variant = 'default',
  size = 'medium',
  labelFontWeight = 'medium',
  onMount,
  isInvalid,
  error,
  style,
  secondaryActionIconOnly,
  withQrCode,
}: FileUploadProps) => {
  const { t } = useLingui();

  const {
    images,
    loadingImageUploads,
    otherFiles,
    handleUpload,
    handleDelete,
    loadingUploads,
    failedUploads,
    createMediumError,
    handleCloseFailedUpload,
    loadingFileUploads,
    startPolling,
  } = useFileUploader({
    uploadData,
    media,
    onChange,
    onUpload,
    onDelete,
    deleteWithApi,
  });

  const ref = useRef<HTMLDivElement>(null);
  const { isMobile } = useViewPort();
  const [isQrCodeDialogOpen, setIsQrCodeDialogOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>();
  const { createQrCodeUrl, isCreateSessionLimitedTokenPending } = useCreateQrCodeUrl();

  const isQrCodeUploadAvailable = !isMobile && withQrCode;

  const onUploadFromPhone = async () => {
    const url = await createQrCodeUrl(uploadData, type ?? 'file', maxNumberOfMedia);

    setQrCodeUrl(url);
    setIsQrCodeDialogOpen(true);
    startPolling();
  };

  useEffect(() => {
    onMount?.(ref);
  }, []);

  const totalNumberOfFiles =
    loadingUploads.length + failedUploads.length + images.length + otherFiles.length;

  const hasMaximumNumberOfMedia =
    maxNumberOfMedia !== undefined && totalNumberOfFiles >= maxNumberOfMedia;

  const allowsMultiple =
    maxNumberOfMedia === undefined || (maxNumberOfMedia !== undefined && maxNumberOfMedia > 1);

  useEffect(() => {
    setIsQrCodeDialogOpen(false);
  }, [totalNumberOfFiles]);

  const shouldShowInput = !disabled && !hasMaximumNumberOfMedia;

  const shouldShowPhotoCardGrid = [...images, ...loadingImageUploads].length > 0;
  const shouldShowInputInsidePhotoCardGrid = variant === 'grid' && shouldShowPhotoCardGrid;

  const FileUploadInput = shouldShowInput ? (
    <InputFile
      ariaLabel={ariaLabel ?? (typeof label === 'string' ? label : '')}
      icon={icon}
      prompt={!shouldShowInputInsidePhotoCardGrid ? prompt : null}
      type={type}
      onUpload={(files) => {
        handleUpload(files);
      }}
      allowsMultiple={allowsMultiple}
      variant={
        variant === 'grid' ? (shouldShowInputInsidePhotoCardGrid ? 'square' : 'default') : 'default'
      }
      size={size}
      isInvalid={isInvalid || !!error}
      isDisabled={showDisabledInput}
      onUploadFromPhone={
        isQrCodeUploadAvailable
          ? () => {
              onUploadFromPhone();
            }
          : undefined
      }
      uploadFromPhoneButtonIconOnly={isQrCodeUploadAvailable && secondaryActionIconOnly}
      isLoadingOnUploadFromPhone={isCreateSessionLimitedTokenPending}
    />
  ) : null;

  const errorMessage =
    createMediumError?.message === 'The file must be smaller than 20 MB'
      ? t({
          id: 'components.file-upload.errors.file-smaller-than-2O-mb',
          message: 'The file must be smaller than 20MB',
        })
      : error;

  return (
    <>
      <Stack gap="0.25rem" ref={ref} style={style}>
        {label && (
          <Paragraph size={theme === 'brand' ? '50' : '100'} weight={labelFontWeight}>
            {label}
          </Paragraph>
        )}
        <Stack gap="0.5rem">
          {!shouldShowInputInsidePhotoCardGrid && FileUploadInput}
          <FilesCards
            failedUploads={failedUploads}
            handleCloseFailedUpload={handleCloseFailedUpload}
            loadingFileUploads={loadingFileUploads}
            otherFiles={otherFiles}
            disabled={disabled}
            handleDelete={handleDelete}
          />
          {shouldShowPhotoCardGrid && (
            <PhotoCardGrid
              loadingUploads={loadingImageUploads.map(
                (loadingImageUpload) => loadingImageUpload.file
              )}
              media={images}
              onDelete={
                disabled
                  ? undefined
                  : (id) => {
                      handleDelete(id);
                    }
              }
              size={size}
              variant="default"
              input={shouldShowInputInsidePhotoCardGrid ? FileUploadInput : undefined}
            />
          )}
          {media.length === 0 && disabled && <Paragraph>-</Paragraph>}
          {!!errorMessage && <Message type="error">{errorMessage}</Message>}
        </Stack>
      </Stack>
      <QrCodeDialog
        url={qrCodeUrl ?? ''}
        setIsOpen={setIsQrCodeDialogOpen}
        isOpen={isQrCodeDialogOpen}
      />
    </>
  );
};

const FilesCards = ({
  failedUploads,
  handleCloseFailedUpload,
  loadingFileUploads,
  otherFiles,
  disabled,
  handleDelete,
}: {
  failedUploads: FileWithError[];
  handleCloseFailedUpload: (index: number) => void;
  loadingFileUploads: FileWithMediumId[];
  otherFiles: Medium[];
  disabled?: boolean;
  handleDelete: (id: string) => void;
}) => {
  return (
    <>
      <FailedUploadsCards
        failedUploads={failedUploads}
        handleCloseFailedUpload={handleCloseFailedUpload}
      />
      <LoadingUploadsCards loadingUploads={loadingFileUploads} />
      <OtherFilesCards otherFiles={otherFiles} disabled={disabled} handleDelete={handleDelete} />
    </>
  );
};

const FailedUploadsCards = ({
  failedUploads,
  handleCloseFailedUpload,
}: {
  failedUploads: FileWithError[];
  handleCloseFailedUpload: (index: number) => void;
}) => {
  const { t } = useLingui();

  return (
    <>
      {failedUploads.map(({ file }, index) => (
        <FileCard
          key={index}
          name={file.name}
          size={file.size}
          error={true}
          onDelete={() => handleCloseFailedUpload(index)}
          ariaLabel={t({
            id: 'components.file-upload.failed',
            message: `Upload failed for file ${file.name}`,
          })}
        />
      ))}
    </>
  );
};

const LoadingUploadsCards = ({ loadingUploads }: { loadingUploads: FileWithMediumId[] }) => {
  return (
    <>
      {loadingUploads.map((loadingUploads) => (
        <FileCard
          key={loadingUploads.file.name}
          name={loadingUploads.file.name}
          size={loadingUploads.file.size}
          loading={true}
        />
      ))}
    </>
  );
};

const OtherFilesCards = ({
  otherFiles,
  disabled,
  handleDelete,
}: {
  otherFiles: Medium[];
  disabled?: boolean;
  handleDelete: (id: string) => void;
}) => {
  return (
    <>
      {otherFiles.map((medium) => (
        <FileCard
          key={medium.id}
          name={medium.originalPath ?? ''}
          url={medium.url}
          size={medium.size}
          onDelete={
            disabled || medium.undeletable
              ? undefined
              : () => {
                  handleDelete(medium.id);
                }
          }
        />
      ))}
    </>
  );
};

export default FileUpload;
